import { useEffect, useState } from "react";
import { Wallet, Loader2, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export const PortalWallet = () => {
  const { t } = useLang();
  const cur = t("portal.currency");
  const [data, setData] = useState(null);
  useEffect(() => { api.portalWallet().then(setData).catch(() => setData({ linked: false, balance: null, ops: [], trend: [] })); }, []);
  const money = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${cur}`;

  if (!data) return <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;

  if (!data.linked || data.balance === null || data.balance === undefined) {
    return (
      <div className="py-14 text-center" data-testid="portal-wallet-empty">
        <Wallet className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-3 text-slate-500 text-sm">{data.linked ? t("portal.wallet.none") : t("portal.wallet.notLinked")}</p>
      </div>
    );
  }

  const neg = (data.balance || 0) < 0;
  return (
    <div className="space-y-4" data-testid="portal-wallet" dir="rtl">
      <div className={`rounded-2xl border p-5 text-center ${neg ? "border-rose-100 bg-rose-50" : "border-emerald-100 bg-emerald-50"}`}>
        <p className={`text-xs ${neg ? "text-rose-700" : "text-emerald-700"} flex items-center justify-center gap-1.5`}><Wallet className="h-4 w-4" /> {t("portal.wallet.balance")}</p>
        <p className={`font-heading text-3xl font-bold font-mono-nums mt-1 ${neg ? "text-rose-700" : "text-emerald-700"}`} data-testid="portal-wallet-balance">{money(data.balance)}</p>
      </div>

      {data.trend?.length > 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold text-slate-600 mb-2">{t("portal.wallet.trend")}</p>
          <div style={{ width: "100%", height: 160 }}>
            <ResponsiveContainer>
              <LineChart data={data.trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={48} />
                <Tooltip formatter={(v) => money(v)} />
                <Line type="monotone" dataKey="balance" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div>
        <p className="font-heading font-bold text-slate-800 mb-2">{t("portal.wallet.ops")}</p>
        {data.ops.length === 0 ? (
          <p className="py-8 text-center text-slate-400 text-sm">{t("portal.wallet.none")}</p>
        ) : (
          <div className="space-y-1.5" data-testid="portal-wallet-ops">
            {data.ops.map((op, i) => {
              const label = op.type === "opening" ? t("portal.wallet.opening")
                : op.change < 0 ? t("portal.wallet.debit")
                  : op.change > 0 ? t("portal.wallet.credit") : t("portal.wallet.same");
              return (
                <div key={i} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2">
                  <div className="flex items-center gap-2">
                    {op.type === "opening" ? (
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">•</span>
                    ) : op.change < 0 ? (
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-rose-100 text-rose-600"><ArrowDownRight className="h-3.5 w-3.5" /></span>
                    ) : op.change > 0 ? (
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><ArrowUpRight className="h-3.5 w-3.5" /></span>
                    ) : (
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400">=</span>
                    )}
                    <div>
                      <p className="text-xs text-slate-700">{label}</p>
                      <p className="text-[10px] text-slate-400 font-mono-nums">{op.date}</p>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-bold font-mono-nums ${op.change < 0 ? "text-rose-600" : op.change > 0 ? "text-emerald-600" : "text-slate-500"}`}>
                      {op.type === "opening" ? money(op.change) : `${op.change > 0 ? "+" : ""}${money(op.change)}`}
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono-nums">{t("portal.wallet.result")}: {money(op.balance)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
